// Use REACT_APP_API_HOST from environment if available, else fallback to window.__API_HOST__ or ""
const API_HOST =
  process.env.REACT_APP_API_HOST ||
  (window as any).__API_HOST__ ||
  ""; // e.g. "http://localhost:8000"
const API_PREFIX = `${API_HOST}/api`;

export interface DeepPreset {
  id: string | number;
  name: string;
  filters: any;
}

export interface DeepExcelSettings {
  id: number;
  deep_preset_id: number;
  summary_wallets_order_column: 'ROI' | 'WINRATE' | 'TRADED_MINTS' | 'LAST_SWAP';
  summary_order_desc: boolean;
  detailed_tokens_order_column: 'ROI' | 'LAST_SWAP' | 'FIRST_SWAP';
  detailed_order_desc: boolean;
  lite_mode: boolean;
  show_only_period_range_tokens: boolean;
}

export interface DeepWalletExportCreated {
  message: string;
  task_id: string;
  queued_for_release: boolean;
  preset: string;
  filters: string;
}

export interface DeepWalletExportTask {
  status: string;
  exception: string | null;
  result_sended: boolean;
  queued_for_release: boolean;
}

function getInitDataHeader() {
  // Telegram WebApp initData header
  // @ts-ignore
  return window?.Telegram?.WebApp?.initData || "query_id=AAHzAbVNAwAAAPMBtU0fDhog&user=%7B%22id%22%3A7746159091%2C%22first_name%22%3A%22Scanner%20%5Balso%20Printer%5D%22%2C%22last_name%22%3A%22%22%2C%22username%22%3A%22scanner527%22%2C%22language_code%22%3A%22ru%22%2C%22is_premium%22%3Atrue%2C%22allows_write_to_pm%22%3Atrue%2C%22photo_url%22%3A%22https%3A%5C%2F%5C%2Ft.me%5C%2Fi%5C%2Fuserpic%5C%2F320%5C%2FWqhH5OpWMHmK_cgNPJpEnj17kBgVRWCT-qTVdcAP8Z2uO330MkXtIvn6BuS36Ink.svg%22%7D&auth_date=1749143863&signature=qVYXGIGwfdpuKjrmh16Lh-3CsR48hBDq9CQBr5rWSmgUeyYStgJBJDqijcUt4gUQnO-L68cIZ01WUyu9laPmBg&hash=3e8d5c465635311e37507d46d9af86e1dfdc08a7748ef96ac2c994da668b1d8b";
}

export const deepPresetsClient = {
  async getPresets(): Promise<Record<string, any>> {
    const res = await fetch(`${API_PREFIX}/deep/presets`, {
      headers: {
        "init-data": getInitDataHeader(),
      },
    });
    if (!res.ok) throw new Error(await res.text());
    return await res.json();
  },

  async getPreset(presetId: string | number): Promise<Record<string, any>> {
    const res = await fetch(`${API_PREFIX}/deep/preset/${presetId}`, {
      headers: {
        "init-data": getInitDataHeader(),
      },
    });
    if (!res.ok) throw new Error(await res.text());
    return await res.json();
  },

  async createPreset(payload: any): Promise<Record<string, any>> {
    const res = await fetch(`${API_PREFIX}/deep/preset`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "init-data": getInitDataHeader(),
      },
      body: JSON.stringify(payload),
    });
    if (!res.ok) throw new Error(await res.text());
    return await res.json();
  },

  async updatePreset(presetId: string | number, payload: any): Promise<Record<string, any>> {
    const res = await fetch(`${API_PREFIX}/deep/preset/${presetId}`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        "init-data": getInitDataHeader(),
      },
      body: JSON.stringify(payload),
    });
    if (res.status === 422) {
      const errorJson = await res.json();
      // Pydantic/FastAPI validation errors are usually in errorJson.detail
      if (Array.isArray(errorJson.detail)) {
        const messages = errorJson.detail.map((err: any) => {
          // err.loc, err.msg, err.type
          const loc = Array.isArray(err.loc) ? err.loc.join('.') : err.loc;
          return `${loc}: ${err.msg}`;
        }).join('; ');
        throw new Error(`Validation error: ${messages}`);
      }
      throw new Error(`Validation error: ${JSON.stringify(errorJson.detail)}`);
    }
    if (!res.ok) throw new Error(await res.text() + ' failed');
    return await res.json();
  },

  async deletePreset(presetId: string | number): Promise<void> {
    const res = await fetch(`${API_PREFIX}/deep/preset/${presetId}`, {
      method: "DELETE",
      headers: {
        "init-data": getInitDataHeader(),
      },
    });
    if (!res.ok) throw new Error(await res.text());
  },

  async getWalletsWinrateStat(): Promise<void> {
    const res = await fetch(`${API_PREFIX}/stat/winrate`, {
      method: "GET",
      headers: {
        "init-data": getInitDataHeader(),
      },
    });
    if (!res.ok) throw new Error(await res.text());
    return await res.json();
  },

  async exportWallets(presetId: string | number, tokens?: string[]): Promise<DeepWalletExportCreated> {
    const hasTokens = Boolean(tokens?.length);
    const res = await fetch(`${API_PREFIX}/deep/preset/${presetId}/export_wallets`, {
      method: "POST",
      headers: {
        "init-data": getInitDataHeader(),
        ...(hasTokens ? { "Content-Type": "application/json" } : {}),
      },
      ...(hasTokens ? { body: JSON.stringify({ tokens }) } : {}),
    });
    if (!res.ok) {
      const body = await res.text();
      let errorJson: any = null;
      try {
        errorJson = JSON.parse(body);
      } catch {
        // Keep the plain response body below.
      }
      const detail = errorJson?.detail;
      if (typeof detail === 'string') throw new Error(detail);
      if (Array.isArray(detail)) {
        throw new Error(detail.map((item: any) => `${item.loc?.slice(1).join('.') || 'Field'}: ${item.msg || 'invalid value'}`).join('; '));
      }
      throw new Error(body || `Export request failed (${res.status})`);
    }
    return await res.json();
  },

  async getWalletExportTask(taskId: string): Promise<DeepWalletExportTask> {
    const res = await fetch(`${API_PREFIX}/deep/export_wallets/${encodeURIComponent(taskId)}`, {
      headers: {
        "init-data": getInitDataHeader(),
      },
    });
    if (!res.ok) throw new Error(await res.text());
    return await res.json();
  },

  exportTokensMcap: async ({
    hours,
    from_dollar_mcap,
    till_dollar_mcap,
    migrate_status = 'any',
    platform = 'PumpFun',
  }: {
    hours: number;
    from_dollar_mcap: number | undefined;
    till_dollar_mcap: number | undefined;
    migrate_status?: string;
    platform?: 'PumpFun' | 'Raydium';
  }) => {
    const params = new URLSearchParams({
      hours: String(hours),
      from_dollar_mcap: String(from_dollar_mcap ? from_dollar_mcap : 0),
      till_dollar_mcap: String(till_dollar_mcap ? till_dollar_mcap : 100_000),
      migrate_status,
      platform,
    });
    const res = await fetch(`${API_PREFIX}/export/tokens/mcap?${params.toString()}`, {
      headers: {
        "init-data": getInitDataHeader(),
      }
    });
    if (!res.ok) throw new Error(await res.text());
    return await res.json();
  },

  async getUser(): Promise<{ telegram_id: number; subscription_end_at: string; subscription_type: string }> {
    const res = await fetch(`${API_PREFIX}/user`, {
      headers: {
        "init-data": getInitDataHeader(),
      },
    });
    if (!res.ok) throw new Error(await res.text());
    return await res.json();
  },

  async exportDevsMcapReport(payload: {
    devs: string[];
    min_percent: number;
    min_mcap: number;
    max_mcap: number;
    from_ts: number;
    till_ts: number;
  }): Promise<Record<string, any>> {
    const res = await fetch(`${API_PREFIX}/export/devs/mcap_report`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'init-data': getInitDataHeader(),
      },
      body: JSON.stringify(payload),
    });
    if (!res.ok) throw new Error(await res.text());
    return await res.json();
  },

  async getExcelSettings(preset_id: number): Promise<DeepExcelSettings> {
    const res = await fetch(`${API_PREFIX}/excel_settings?preset_id=${preset_id}`, {
      headers: {
        "init-data": getInitDataHeader(),
      },
    });
    if (!res.ok) throw new Error(await res.text());
    const settings: DeepExcelSettings = await res.json();

    // Keep older API responses backward compatible until every saved row has
    // the new column populated.
    return {
      ...settings,
      show_only_period_range_tokens: settings.show_only_period_range_tokens ?? false,
    };
  },

  async saveExcelSettings(settings: DeepExcelSettings): Promise<unknown> {
    const res = await fetch(`${API_PREFIX}/excel_settings`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "init-data": getInitDataHeader(),
      },
      body: JSON.stringify(settings),
    });
    if (!res.ok) throw new Error(await res.text());
    return await res.json();
  },

  async duplicatePreset(presetId: string | number): Promise<Record<string, any>> {
    const res = await fetch(`${API_PREFIX}/deep/preset/${presetId}/duplicate`, {
      method: "POST",
      headers: {
        "init-data": getInitDataHeader(),
      },
    });
    if (!res.ok) throw new Error(await res.text());
    return await res.json();
  },
};
